/**
 * @file - Connect Button Component.
 */

import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { DialogHandlerService } from '../../../services/dialog-handler/dialog-handler.service';
import { environment } from '../../../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { DriverService } from '../../../services/driver/driver.service';
import { UserDataService } from '../../../services/user-data/user-data.service';
import { PageSourceService } from '../../../services/driver/page-source.service';
import { RoutePaths } from '../../../../shared/constants/route-paths.constant';

/**
 * Connect Button Component.
 */
@Component({
    standalone: false,
    selector: 'uber-connect-button',
    templateUrl: './connect-button.component.html',
    styleUrls: ['./connect-button.component.scss'],
})
export class ConnectButtonComponent {
    uberConnectFlag: boolean = environment.UBER_CONNECT_SIGNUP_FLAG;
    uberLink: string = environment.UBER_LINK;
    currentPageSource: string = '';
    uberInfo: string = `
You will be sharing your name, phone number, email address, profile picture, preferred language, vehicle(s) make/model/year and activation status (active, onboarding, etc.) with Octopus.<br><br>Octopus will use this information to connect your Uber account to the Octopus tablet and display the Uber Journey TV experience to your Uber riders, in accordance with  <a href="https://www.uber.com/legal/en/document/?name=privacy-notice&country=united-states&lang=en" target="_blank" class="pink">Uber's Privacy Notice</a>.
`;
    @Input() isSignup: boolean = true;
    @Input() isHideSuccess: boolean = false;

    private readonly _uberConnectStatus: BehaviorSubject<string> =
        new BehaviorSubject<string>('');
    /**
     * Set Uber connect status.
     */
    @Input()
    set uberConnectStatus(value: string) {
        this._uberConnectStatus.next(value);
    }

    /**
     * Get Uber connect status.
     */
    get uberConnectStatus(): string {
        return this._uberConnectStatus.getValue();
    }

    isConnected: boolean | null = null;

    private readonly _showAlreadyConnected: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    isRemoveAccessHidden: boolean = false;
    /**
     * Show already connected.
     */
    @Input()
    set showAlreadyConnected(value: boolean) {
        // If same value, do not update
        if (this._showAlreadyConnected.getValue() === value) {
            return;
        }
        this._showAlreadyConnected.next(value);
        if (value) {
            this.isRemoveAccessHidden = false;
        }
    }

    /**
     * Get show already connected.
     */
    get showAlreadyConnected(): boolean {
        return this._showAlreadyConnected.getValue();
    }

    private readonly _connectedAccountPhone: BehaviorSubject<string> =
        new BehaviorSubject<string>('');
    /**
     * Set connected account phone.
     */
    @Input()
    set connectedAccountPhone(value: string) {
        // If same phone, do not update
        if (this._connectedAccountPhone.getValue() === value) {
            return;
        }
        this._connectedAccountPhone.next(value);
    }

    /**
     * Get connected account phone.
     */
    get connectedAccountPhone(): string {
        return this._connectedAccountPhone.getValue();
    }

    private readonly _loggedUserEmailDomain: BehaviorSubject<string> =
        new BehaviorSubject<string>('');
    /**
     * Set logged user email.
     */
    @Input()
    set loggedUserEmailDomain(value: string) {
        // If same email, do not update
        if (this._loggedUserEmailDomain.getValue() === value) {
            return;
        }
        this._loggedUserEmailDomain.next(value);
    }

    /**
     * Get logged user email.
     */
    get loggedUserEmail(): string {
        return this._loggedUserEmailDomain.getValue();
    }

    private readonly _isReferredByUberPartner: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    /**
     * Set is referred by Uber partner.
     */
    @Input()
    set isReferredByUberPartner(value: boolean) {
        // If same value, do not update
        if (this._isReferredByUberPartner.getValue() === value) {
            return;
        }
        this._isReferredByUberPartner.next(value);
    }

    /**
     * Get is referred by Uber partner.
     */
    get isReferredByUberPartner(): boolean {
        return this._isReferredByUberPartner.getValue();
    }

    /**
     * Constructor.
     *
     * @param {DriverService} driverService - Driver service.
     * @param {DialogHandlerService} dialogService - Dialog service.
     * @param {Router} router - Router.
     * @param {UserDataService} userService - User service.
     * @param {PageSourceService} pageSourceService - Page source service.
     */
    constructor(
        private readonly driverService: DriverService,
        private readonly dialogService: DialogHandlerService,
        private readonly router: Router,
        private readonly userService: UserDataService,
        private readonly pageSourceService: PageSourceService,
        private readonly cdr: ChangeDetectorRef
    ) {
        this._uberConnectStatus.subscribe((status: string) => {
            this.isConnected = status
                ? status.toUpperCase() === 'CONNECTED'
                : null;
        });

        this._showAlreadyConnected.subscribe((show: boolean) => {
            this.showAlreadyConnected = show;
        });

        this._connectedAccountPhone.subscribe((phone: string) => {
            this.connectedAccountPhone = phone;
        });

        this._loggedUserEmailDomain.subscribe((emailDomain: string) => {
            this.uberConnectFlag =
                (this.isSignup
                    ? environment.UBER_CONNECT_SIGNUP_FLAG
                    : environment.UBER_CONNECT_DASHBOARD_FLAG) ||
                environment.ALLOWED_DOMAINS.includes(emailDomain);
        });
        
        this._isReferredByUberPartner.subscribe((isReferred: boolean) => {
            this.isReferredByUberPartner = isReferred;
        });
        this.currentPageSource = this.pageSourceService.getPageSourceForUber();
    }

    /**
     * Open a dialog with the information.
     */
    showInfo(): void {
        if (this.isSignup) {
            this.dialogService
                .confirmAction({
                    title: 'Uber Account Linking Info',
                    message: this.uberInfo,
                    cancelBtnText: 'Skip',
                    confirmBtnText: 'Link',
                })
                .subscribe((data) => {
                    if (data) {
                        this.connectUber();
                    }
                });
        } else {
            this.router.navigate([RoutePaths.UBER_LINK]);
        }
    }

    /**
     * Connect Uber Account.
     */
    connectUber(): void {
        // Create an encrypted string that contains the current page
        let stateDict = {
            sourcePage: globalThis.location.pathname.split('/').pop(),
            isApplication: [
                'tos_complete',
                'driver_info_complete',
                'profile_complete',
                'payment_complete',
            ].includes(localStorage.getItem('status') || ''),
            isReferredByUberPartner: this.isReferredByUberPartner,
        };
        const state = btoa(JSON.stringify(stateDict));

        // Go to the Uber link
        const nextUrl = encodeURIComponent(`${this.uberLink}&state=${state}`);
        globalThis.open(`${environment.UBER_LOGOUT_URL}?next_url=${nextUrl}`, '_self');
    }

    /**
     * Remove Uber Account.
     */
    removeAccess(): void {
        this.dialogService
            .showUberDisconnectDialog({
                title: 'Unlinking Stops JourneyTV and Earnings',
                cancelBtnText: 'Cancel',
                confirmBtnText: 'Confirm',
                source: this.currentPageSource
            })
            .subscribe((result) => {
                if (result && result.confirmed && result.success) {
                    this.isRemoveAccessHidden = true;
                    this.isConnected = false;
                    this._showAlreadyConnected.next(false);
                    this.cdr.detectChanges();
                    this.userService.fetchUserSummary().subscribe();
                }
            });
    }
}
